<template>
  <HrPageLayout
    title="Extended Absence Report"
    subtitle="Employees with extended absence"
    icon="⚠️"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'HR Management' },
      { label: 'Extended Absence Report' }
    ]"
  >
    <template #header-actions>
      <button @click="refreshData" class="hr-btn hr-btn-primary hr-me-2">
        <i class="bx bx-refresh hr-me-1"></i> Refresh
      </button>
      <button @click="exportReport" class="hr-btn hr-btn-success">
        <i class="bx bx-export hr-me-1"></i> Export
      </button>
    </template>

    <div class="off-duty-content">
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">Report Filters</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-form-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="filters.departmentId" class="hr-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Month</label>
              <input v-model="filters.selectedMonth" type="month" class="hr-input">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Threshold (Days)</label>
              <input v-model.number="filters.absenceThreshold" type="number" min="1" max="60" class="hr-input">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Search</label>
              <input type="text" v-model="filters.search" class="hr-input" placeholder="Search by name or ID...">
            </div>
          </div>
        </div>
      </div>

      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-user-x"></i></div>
          <p class="hr-metric-label">Extended Absences</p>
          <h2 class="hr-metric-value">{{ summary.totalExtendedAbsences || 0 }}</h2>
          <p class="hr-metric-hint">{{ summary.absencePercentage || 0 }}% of workforce</p>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
          <p class="hr-metric-label">High Risk (30+ days)</p>
          <h2 class="hr-metric-value">{{ summary.highRiskCount || 0 }}</h2>
          <p class="hr-metric-hint hr-text-danger">Immediate attention</p>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-error"></i></div>
          <p class="hr-metric-label">Medium Risk (20-29 days)</p>
          <h2 class="hr-metric-value">{{ summary.mediumRiskCount || 0 }}</h2>
          <p class="hr-metric-hint hr-text-warning">Requires follow-up</p>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-info-circle"></i></div>
          <p class="hr-metric-label">Low Risk (15-19 days)</p>
          <h2 class="hr-metric-value">{{ summary.lowRiskCount || 0 }}</h2>
          <p class="hr-metric-hint hr-text-info">Monitor situation</p>
        </div>
      </div>

      <div class="hr-content-grid hr-mb-4" style="grid-template-columns: repeat(2, 1fr);">
        <div class="hr-chart-card">
          <div class="hr-chart-header"><h5 class="hr-chart-title">Department Breakdown</h5></div>
          <div class="hr-chart-body">
            <div v-if="summary.departmentAbsences && summary.departmentAbsences.length > 0" class="hr-table-wrapper">
              <table class="hr-table hr-table-sm">
                <thead><tr><th>Department</th><th class="hr-text-center">Count</th><th class="hr-text-center">%</th></tr></thead>
                <tbody>
                  <tr v-for="dept in summary.departmentAbsences" :key="dept.departmentName">
                    <td>{{ dept.departmentName }}</td>
                    <td class="hr-text-center">{{ dept.count }}</td>
                    <td class="hr-text-center">{{ ((dept.count / summary.totalExtendedAbsences) * 100).toFixed(1) }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="hr-empty-state"><p class="hr-text-muted">No department data available</p></div>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-header"><h5 class="hr-chart-title">Risk Level Summary</h5></div>
          <div class="hr-chart-body">
            <div v-if="summary.totalExtendedAbsences > 0">
              <div class="hr-mb-3">
                <div class="hr-flex hr-flex-between"><span>High Risk (30+ days)</span><span class="hr-text-danger hr-fw-bold">{{ summary.highRiskCount }}</span></div>
                <div class="hr-progress-bar hr-mt-1"><div class="hr-progress-fill hr-badge-danger" :style="`width: ${(summary.highRiskCount / summary.totalExtendedAbsences) * 100}%`"></div></div>
              </div>
              <div class="hr-mb-3">
                <div class="hr-flex hr-flex-between"><span>Medium Risk (20-29 days)</span><span class="hr-text-warning hr-fw-bold">{{ summary.mediumRiskCount }}</span></div>
                <div class="hr-progress-bar hr-mt-1"><div class="hr-progress-fill hr-badge-warning" :style="`width: ${(summary.mediumRiskCount / summary.totalExtendedAbsences) * 100}%`"></div></div>
              </div>
              <div class="hr-mb-0">
                <div class="hr-flex hr-flex-between"><span>Low Risk (15-19 days)</span><span class="hr-text-info hr-fw-bold">{{ summary.lowRiskCount }}</span></div>
                <div class="hr-progress-bar hr-mt-1"><div class="hr-progress-fill hr-badge-info" :style="`width: ${(summary.lowRiskCount / summary.totalExtendedAbsences) * 100}%`"></div></div>
              </div>
            </div>
            <div v-else class="hr-empty-state"><p class="hr-text-muted">No risk data available</p></div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">Employees with Extended Absence ({{ filters.absenceThreshold }}+ days)</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading absence data...</p>
          </div>
          <div v-else-if="!extendedAbsenceEmployees.length" class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-user-x"></i></div>
            <h5>No employees with extended absence</h5>
            <p class="hr-text-muted">Try adjusting your filters or threshold settings</p>
          </div>
          <div v-else>
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Contact</th>
                  <th class="hr-text-center">Absent Days</th>
                  <th>Risk Level</th>
                  <th>Last Seen</th>
                  <th class="hr-text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="employee in extendedAbsenceEmployees" :key="employee.employeeId">
                  <td>
                    <div><strong>{{ employee.employeeName }}</strong></div>
                    <small class="hr-text-muted">ID: {{ employee.employeeId }}</small>
                    <small v-if="employee.designation" class="d-block hr-text-muted">{{ employee.designation }}</small>
                  </td>
                  <td>{{ employee.departmentName }}</td>
                  <td>
                    <div v-if="employee.email"><i class="bx bx-envelope hr-me-1"></i>{{ employee.email }}</div>
                    <div v-if="employee.phone"><i class="bx bx-phone hr-me-1"></i>{{ formatMobileKe(employee.phone) }}</div>
                    <div v-if="!employee.email && !employee.phone" class="hr-text-muted">No contact info</div>
                  </td>
                  <td class="hr-text-center">
                    <span class="hr-badge" :class="getAbsenceBadgeClass(employee.consecutiveAbsentDays)">{{ employee.consecutiveAbsentDays }} days</span>
                  </td>
                  <td><span class="hr-badge" :class="getRiskBadgeClass(employee.riskLevel)">{{ employee.riskLevel }}</span></td>
                  <td>
                    <div v-if="employee.lastAttendance">{{ formatDate(employee.lastAttendance) }}<small class="d-block hr-text-muted">{{ getDaysAgo(employee.lastAttendance) }}</small></div>
                    <div v-else class="hr-text-danger">No record</div>
                  </td>
                  <td class="hr-text-center">
                    <button @click="viewDetails(employee)" class="hr-btn hr-btn-sm hr-btn-info hr-me-1" title="View Details"><i class="bx bx-show"></i></button>
                    <button @click="contactEmployee(employee)" class="hr-btn hr-btn-sm hr-btn-primary" title="Contact Employee" :disabled="!employee.email && !employee.phone"><i class="bx bx-send"></i></button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <div v-if="summary.totalPages > 1" class="hr-pagination-wrap hr-justify-center hr-mt-4">
              <nav class="hr-pagination">
                <button type="button" class="hr-page-btn" :disabled="filters.page <= 1" @click.prevent="changePage(filters.page - 1)">Previous</button>
                <button v-for="page in getPageNumbers()" :key="page" type="button" class="hr-page-btn" :class="{ active: page === filters.page }" @click.prevent="changePage(page)">{{ page }}</button>
                <button type="button" class="hr-page-btn" :disabled="filters.page >= summary.totalPages" @click.prevent="changePage(filters.page + 1)">Next</button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Employee Details Modal (Bootstrap) -->
          <div class="modal fade" id="detailsModal" tabindex="-1">
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Employee Absence Details</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)"></button>
                </div>
                <div class="modal-body">
                  <div v-if="selectedEmployee">
                    <div class="row mb-4">
                      <div class="col-md-6">
                        <h5>{{ selectedEmployee.employeeName }}</h5>
                        <p class="text-muted mb-1">{{ selectedEmployee.designation || 'No Designation' }}</p>
                        <p class="text-muted mb-1">ID: {{ selectedEmployee.employeeId }}</p>
                        <p class="text-muted">Dept: {{ selectedEmployee.departmentName }}</p>
                      </div>
                      <div class="col-md-6 text-md-end">
                        <span class="badge bg-danger fs-6 mb-2">{{ selectedEmployee.consecutiveAbsentDays }} Days Absent</span>
                        <p v-if="selectedEmployee.email" class="mb-1">
                          <i class="mdi mdi-email-outline me-1"></i>{{ selectedEmployee.email }}
                        </p>
                        <p v-if="selectedEmployee.phone" class="mb-0">
                          <i class="mdi mdi-phone-outline me-1"></i>{{ formatMobileKe(selectedEmployee.phone) }}
                        </p>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-6">
                        <h6>Absence Information</h6>
                        <ul class="list-unstyled">
                          <li class="mb-2">
                            <strong>Consecutive Absent Days:</strong> {{ selectedEmployee.consecutiveAbsentDays }}
                          </li>
                          <li class="mb-2">
                            <strong>Risk Level:</strong>
                            <span class="badge" :class="getRiskBadgeClass(selectedEmployee.riskLevel)">
                              {{ selectedEmployee.riskLevel }}
                            </span>
                          </li>
                          <li class="mb-2">
                            <strong>Last Attendance:</strong>
                            <span v-if="selectedEmployee.lastAttendance">
                              {{ formatDate(selectedEmployee.lastAttendance) }}
                            </span>
                            <span v-else class="text-danger">No record</span>
                          </li>
                        </ul>
                      </div>
                      <div class="col-md-6">
                        <h6>Recent Leave Records</h6>
                        <div v-if="selectedEmployee.recentLeaves && selectedEmployee.recentLeaves.length">
                          <div v-for="leave in selectedEmployee.recentLeaves" :key="leave.startDate" class="mb-2">
                            <div class="small">
                              <strong>{{ leave.leaveTypeName }}</strong><br>
                              {{ formatDate(leave.startDate) }} to {{ formatDate(leave.endDate) }}
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-muted">No recent approved leaves</div>
                      </div>
                    </div>

                    <div v-if="selectedEmployee.absentDates && selectedEmployee.absentDates.length" class="mt-4">
                      <h6>Recent Absent Dates</h6>
                      <div class="row">
                        <div v-for="absent in selectedEmployee.absentDates.slice(-10)" :key="absent.date" class="col-md-4 mb-1">
                          <small>
                            <i class="mdi mdi-calendar-remove text-danger me-1"></i>
                            {{ formatDate(absent.date) }} ({{ absent.dayOfWeek }})
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
                  <button type="button" class="btn btn-primary" @click="contactEmployee(selectedEmployee)">
                    Contact Employee
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Modal -->
          <div class="modal fade" id="contactModal" tabindex="-1">
            <div class="modal-dialog">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Contact Employee</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)"></button>
                </div>
                <div class="modal-body">
                  <div v-if="selectedEmployee">
                    <div class="text-center mb-4">
                      <h5>{{ selectedEmployee.employeeName }}</h5>
                      <p class="text-muted">{{ selectedEmployee.departmentName }}</p>
                    </div>

                    <form @submit.prevent="sendNotification">
                      <div class="mb-3">
                        <label class="form-label">Contact Method</label>
                        <div class="btn-group w-100" role="group">
                          <input type="radio" class="btn-check" id="email" value="email" v-model="contactForm.method">
                          <label class="btn btn-outline-primary" for="email">Email</label>
                          <input type="radio" class="btn-check" id="sms" value="sms" v-model="contactForm.method">
                          <label class="btn btn-outline-primary" for="sms">SMS</label>
                          <input type="radio" class="btn-check" id="both" value="both" v-model="contactForm.method">
                          <label class="btn btn-outline-primary" for="both">Both</label>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Template</label>
                        <select v-model="contactForm.template" class="form-select" @change="updateTemplate">
                          <option value="reminder">Attendance Reminder</option>
                          <option value="warning">Formal Warning</option>
                          <option value="welfare">Welfare Check</option>
                          <option value="custom">Custom Message</option>
                        </select>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Subject</label>
                        <input type="text" v-model="contactForm.subject" class="form-control" required>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Message</label>
                        <textarea v-model="contactForm.message" class="form-control" rows="4" required></textarea>
                      </div>

                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="contactForm.sendToManager" id="sendToManager">
                        <label class="form-check-label" for="sendToManager">
                          Send copy to department manager
                        </label>
                      </div>
                    </form>
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-light" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Cancel</button>
                  <button type="button" class="btn btn-primary" @click="sendNotification" :disabled="sending">
                    <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                    Send Message
                  </button>
                </div>
              </div>
            </div>
          </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import * as bootstrap from 'bootstrap';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, formatMobileKe } from "@/api";
import _ from 'lodash';

export default {
  name: "ExtendedAbsenceReport",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      sending: false,
      extendedAbsenceEmployees: [],
      departments: [],
      summary: {},
      selectedEmployee: null,
      filters: {
        departmentId: "",
        selectedMonth: this.getCurrentMonth(),
        absenceThreshold: 15,
        search: "",
        page: 1,
        rows_per_page: 10
      },
      contactForm: {
        method: "email",
        template: "reminder",
        subject: "",
        message: "",
        sendToManager: false
      },
      messageTemplates: {
        reminder: {
          subject: "Important: Absence Notification",
          message: "Dear {name},\n\nOur records indicate that you have been absent from work for {days} consecutive working days without approved leave. Please contact HR as soon as possible.\n\nBest regards,\nHR Department"
        },
        warning: {
          subject: "Formal Absence Warning",
          message: "Dear {name},\n\nThis is a formal notification regarding your continued absence from work for {days} consecutive working days. Please contact HR immediately.\n\nBest regards,\nHR Department"
        },
        welfare: {
          subject: "Welfare Check",
          message: "Dear {name},\n\nWe haven't seen you at work for {days} days and we're concerned about your wellbeing. Please let us know if you need any support.\n\nBest regards,\nHR Department"
        },
        custom: {
          subject: "",
          message: ""
        }
      }
    };
  },
  mounted() {
    this.getDepartments();
    this.getExtendedAbsenceData();
  },
  methods: {
    getCurrentMonth() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            }
          })
          .catch((error) => {
            console.error("Error fetching departments:", error);
          });
    },

    getExtendedAbsenceData() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getExtendedAbsenceReport");
      data.append("selectedMonth", this.filters.selectedMonth);
      data.append("absenceThreshold", this.filters.absenceThreshold);
      data.append("page", this.filters.page);
      data.append("rows_per_page", this.filters.rows_per_page);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }
      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.extendedAbsenceEmployees = res.data.data.extendedAbsenceEmployees || [];
              this.summary = res.data.data.summary || {};
            } else {
              console.error("Error:", res.data.message);
              this.extendedAbsenceEmployees = [];
              this.summary = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.extendedAbsenceEmployees = [];
            this.summary = {};
          });
    },

    refreshData() {
      this.getExtendedAbsenceData();
    },

    changePage(page) {
      if (page < 1 || page > this.summary.totalPages || page === this.filters.page) return;
      this.filters.page = page;
      this.getExtendedAbsenceData();
    },

    getPageNumbers() {
      const totalPages = this.summary.totalPages || 0;
      const currentPage = this.filters.page;
      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      // Simple pagination logic
      const pages = [];
      if (currentPage > 2) pages.push(1, '...');
      for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 1) pages.push('...', totalPages);
      return pages;
    },

    viewDetails(employee) {
      this.selectedEmployee = employee;
      new bootstrap.Modal(document.getElementById('detailsModal')).show();
    },

    contactEmployee(employee) {
      this.selectedEmployee = employee;
      this.updateTemplate();
      new bootstrap.Modal(document.getElementById('contactModal')).show();
    },

    updateTemplate() {
      if (!this.selectedEmployee) return;

      const template = this.messageTemplates[this.contactForm.template];
      if (!template) return;

      this.contactForm.subject = template.subject;
      let message = template.message;
      message = message.replace(/{name}/g, this.selectedEmployee.employeeName);
      message = message.replace(/{days}/g, this.selectedEmployee.consecutiveAbsentDays);
      this.contactForm.message = message;
    },

    sendNotification() {
      if (!this.selectedEmployee || !this.contactForm.subject || !this.contactForm.message) return;

      this.sending = true;

      const data = new FormData();
      data.append("function", "sendEmployeeNotification");
      data.append("employeeId", this.selectedEmployee.employeeId);
      data.append("subject", this.contactForm.subject);
      data.append("message", this.contactForm.message);
      data.append("contactMethod", this.contactForm.method);
      data.append("sendToManager", this.contactForm.sendToManager ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            this.sending = false;
            if (res.data.success) {
              this.$toast.success("Message sent successfully");
              const modal = bootstrap.Modal.getInstance(document.getElementById('contactModal'));
              if (modal) modal.hide();
            } else {
              this.$toast.error("Failed to send message: " + res.data.message);
            }
          })
          .catch((error) => {
            this.sending = false;
            console.error("Error sending notification:", error);
            this.$toast.error("Network error when sending message");
          });
    },

    exportReport() {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += "Employee ID,Employee Name,Department,Email,Phone,Consecutive Absent Days,Risk Level,Last Attendance\n";

      this.extendedAbsenceEmployees.forEach(employee => {
        csvContent += [
          employee.employeeId,
          `"${employee.employeeName}"`,
          `"${employee.departmentName}"`,
          employee.email || 'N/A',
          formatMobileKe(employee.phone) || 'N/A',
          employee.consecutiveAbsentDays,
          employee.riskLevel,
          employee.lastAttendance ? this.formatDate(employee.lastAttendance) : 'No record'
        ].join(',') + "\n";
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `extended-absence-report-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },

    getDaysAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    },

    getAbsenceBadgeClass(days) {
      if (days >= 30) return 'hr-badge-danger';
      if (days >= 20) return 'hr-badge-warning';
      return 'hr-badge-info';
    },

    getRiskBadgeClass(riskLevel) {
      switch(riskLevel) {
        case 'High': return 'hr-badge-danger';
        case 'Medium': return 'hr-badge-warning';
        case 'Low': return 'hr-badge-info';
        default: return 'hr-badge-secondary';
      }
    }
  },
  watch: {
    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 300),

    'filters.selectedMonth': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 300),

    'filters.absenceThreshold': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 500)
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.off-duty-content { padding: 0; }
.hr-metric-hint { font-size: 0.8rem; margin-top: 4px; color: #64748b; }
.hr-fw-bold { font-weight: 600; }
.hr-justify-center { justify-content: center; }
.hr-me-1 { margin-right: 4px; }

.avatar-sm {
  height: 3rem;
  width: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  border-radius: 50%;
}

@media (max-width: 768px) {
  .table-responsive {
    border: 0;
  }
}
</style>